import { useState } from 'react';

import { Paper } from '@mui/material';

import { ConfirmationDialog } from '@/features/Dashboard/pages/Edit/components';
import { exportPreviewApi } from '@/features/DataHub/api/exportPreview.api';
import type { PastExportDetails } from '@/features/DataHub/api/pastExports.api';
import { templatesApi } from '@/features/DataHub/api/templates.api';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import { useExport } from '@/features/DataHub/modules/Export/context/ExportContext';
import { formatDateForFilename } from '@/features/DataHub/modules/Export/hooks/useDownloadFileContent';
import { pythonPreviewService } from '@/features/DataHub/modules/Export/services';
import { ExportItemActions } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemActions';
import { ExportItemDetails } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemDetails';
import { ExportItemHeader } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemHeader';
import { useDeletePastExport, usePastExportDetails } from '@/features/DataHub/queries';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import buildUrl from '@/utils/buildUrl';

const CLIENT_SIDE_EXPORT_TYPES = new Set(['csv', 'curl', 'python']);

const downloadContent = (content: string, fileType: 'csv' | 'json' | 'py') => {
  const mimeTypes = {
    csv: 'text/csv;charset=utf-8;',
    json: 'application/json;charset=utf-8;',
    py: 'text/plain;charset=utf-8;'
  };
  const timestamp = formatDateForFilename(new Date());
  const blob = new Blob([content], { type: mimeTypes[fileType] });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `GX-Data_${timestamp}.${fileType}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const combineCsvData = (responses: string[]): string => {
  if (!responses.length) return '';
  const [firstResponse, ...otherResponses] = responses;
  const [header, ...rows] = firstResponse.split('\n').filter(Boolean);
  const combinedRows = [...rows];
  for (const response of otherResponses) {
    const [, ...dataRows] = response.split('\n');
    combinedRows.push(...dataRows.filter(Boolean));
  }
  return [header, ...combinedRows].join('\n');
};

const fetchAndDownloadCsvJson = async (data: PastExportDetails, isCsv: boolean) => {
  const headers = isCsv ? { Accept: 'text/csv' } : undefined;
  const requests = data.symbols
    .filter(({ Code }) => !!Code)
    .map(({ Code, Period, PeriodType, TimeRef }) => {
      const indexURL = buildUrl('/index', {
        code: Code,
        period: Period,
        periodType: PeriodType,
        timeRef: TimeRef,
        from: data.from,
        timezone: data.timezone,
        to: data.to,
        metadata: data.metadata === 'true'
      });
      return exportPreviewApi.fetchIndexData(indexURL, headers);
    });

  const results = await Promise.all(requests);

  if (isCsv) {
    downloadContent(combineCsvData(results), 'csv');
  } else {
    const flattenedData = results.flat();
    downloadContent(JSON.stringify(flattenedData, null, 2), 'json');
  }
};

export const ExportItem = ({ item }: { item: ExportItemType }) => {
  const { exportDataWithParams, setExcelExportStatus } = useExport();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading } = usePastExportDetails(item.id);
  const { mutate, isPending: isDeleting } = useDeletePastExport();

  const handleDelete = () => {
    setConfirmOpen(false);
    mutate(item.id);
  };

  const handleRegenerate = async () => {
    if (!data) return;
    setExcelExportStatus('loading');

    // For client-side export types (csv, curl, python), save first then download
    if (CLIENT_SIDE_EXPORT_TYPES.has(data.type)) {
      try {
        // First save to past exports
        await exportDataWithParams({
          id: item.id,
          name: data.name,
          type: data.type,
          exportSubType: data.exportSubType,
          from: data.from,
          to: data.to,
          includeMetadata: data.metadata === 'true',
          timezone: data.timezone,
          dateFormat: data.dateFormat,
          symbols: data.symbols
        });

        // Then download the file
        if (data.type === 'csv') {
          await fetchAndDownloadCsvJson(data, true);
        } else if (data.type === 'curl') {
          await fetchAndDownloadCsvJson(data, false);
        } else if (data.type === 'python') {
          const template = await templatesApi.fetchPythonTemplate();
          const result = pythonPreviewService.generateFromTemplate(template, {
            symbols: data.symbols,
            rangeStart: data.from,
            rangeEnd: data.to || '',
            timezone: data.timezone,
            includeMetadata: data.metadata === 'true',
            apiKey: '<YOUR_GX_API_TOKEN>',
            gxURL: ''
          });
          downloadContent(result.content, 'py');
        }
        setExcelExportStatus('idle');
      } catch {
        setExcelExportStatus('idle');
      }
      return;
    }

    // For server-side exports (excel, etc.), let the server handle it
    await exportDataWithParams({
      id: item.id,
      name: data.name,
      type: data.type,
      exportSubType: data.exportSubType,
      from: data.from,
      to: data.to,
      includeMetadata: data.metadata === 'true',
      timezone: data.timezone,
      dateFormat: data.dateFormat,
      symbols: data.symbols
    });
  };

  const option = data ? (optionById.get(data.type) ?? null) : null;
  const isOngoing = item.exportStatus === 'ongoing';

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <ExportItemHeader
        name={item.name}
        status={item.exportStatus}
      />
      <ExportItemDetails
        data={data}
        isLoading={isLoading}
        optionLabel={option?.label}
      />
      <ExportItemActions
        canDelete={!isOngoing || data === undefined}
        canRegenerate={
          (item.exportStatus === 'finished' || item.exportStatus === 'failed') && Boolean(data)
        }
        isDeleting={isDeleting}
        onDelete={() => {
          setConfirmOpen(true);
        }}
        onRegenerate={handleRegenerate}
      />
      <ConfirmationDialog
        cancelText='Cancel'
        confirmText='Delete'
        open={confirmOpen}
        text='This export will be permanently deleted.'
        title='Delete past export?'
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={handleDelete}
      />
    </Paper>
  );
};
